import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';

/**
 * useAuth — single source of truth for "who am I?".
 *
 * Phase 7 fix: this is now a React Context. EVERY component that
 * calls useAuth() reads from the same state. Previously each call
 * had its own state + its own /me fetch, which caused a race in
 * RequireMinRoleRoute (useAuth and useRole each had separate loading
 * states; one would finish while the other was still null and the
 * role check would redirect back to /pos).
 *
 * Wrap the app in <AuthProvider> at the top of the tree (main.tsx).
 */

export type UserRole = 'CASHIER' | 'SUPERVISOR' | 'MANAGER' | 'ADMIN' | 'OWNER';

export interface AuthState {
  userId: string;
  name: string;
  role: UserRole;
  branchId: string;
  branchName?: string;
  stationId?: string | null;
  elevated?: boolean;
  /** Backwards-compat: some components still call these. */
  cashierId: string;
  cashierName: string;
}

interface MeResponse {
  user: {
    id: string;
    name: string;
    role: UserRole;
    branchId: string;
    branch?: { name: string; code: string };
  };
  sessionId: string;
  stationId?: string | null;
  elevated?: boolean;
}

const ME_URL = '/api/auth/me';
const PIN_LOGIN_URL = '/api/auth/login/pin';
const PASSWORD_LOGIN_URL = '/api/auth/login/password';
const LOGOUT_URL = '/api/auth/logout';

function toAuthState(me: MeResponse): AuthState {
  return {
    userId: me.user.id,
    name: me.user.name,
    role: me.user.role,
    branchId: me.user.branchId,
    branchName: me.user.branch?.name,
    stationId: me.stationId,
    elevated: me.elevated,
    cashierId: me.user.id,
    cashierName: me.user.name,
  };
}

async function fetchMe(): Promise<AuthState | null> {
  try {
    const r = await fetch(ME_URL, { credentials: 'include' });
    if (!r.ok) return null;
    const me = (await r.json()) as MeResponse;
    return toAuthState(me);
  } catch {
    return null;
  }
}

interface AuthContextValue {
  auth: AuthState | null;
  isAuthenticated: boolean;
  loading: boolean;
  signInWithPin: (branchId: string, pin: string, stationId?: string) => Promise<AuthState | null>;
  signInWithPassword: (email: string, password: string) => Promise<AuthState | null>;
  signOut: () => Promise<void>;
  refresh: () => Promise<AuthState | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // On mount, ask the server who we are. Exactly once for the whole app.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await fetchMe();
      if (!cancelled) {
        setState(me);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const signInWithPin = useCallback(async (branchId: string, pin: string, stationId?: string) => {
    const r = await fetch(PIN_LOGIN_URL, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ branchId, pin, stationId }),
    });
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      throw new Error(body.error ?? 'Login failed');
    }
    const me = await fetchMe();
    setState(me);
    return me;
  }, []);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    const r = await fetch(PASSWORD_LOGIN_URL, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      throw new Error(body.error ?? 'Login failed');
    }
    const me = await fetchMe();
    setState(me);
    return me;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await fetch(LOGOUT_URL, { method: 'POST', credentials: 'include' });
    } catch { /* ignore network errors on logout */ }
    setState(null);
  }, []);

  const refresh = useCallback(async () => {
    const me = await fetchMe();
    setState(me);
    return me;
  }, []);

  return (
    <AuthContext.Provider value={{
      auth: state,
      isAuthenticated: state !== null,
      loading,
      signInWithPin,
      signInWithPassword,
      signOut,
      refresh,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>. Wrap your app root in <AuthProvider>.');
  }
  return ctx;
}
